import { forwardRef, useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardRowSimple,
  formatDateTimeFromISO,
  Logos,
  TabDialog,
  type TabDialogProps,
  TabItem,
  Tag,
  TagIntent,
} from "widgets";
import { useDOMRef } from "widgets/hooks";

import { getClientSetup } from "../../../app-utils/browser-compatibility.js";
import { capitalizeFirstLetter } from "../../../app-utils/string.js";
import { type AppConfiguration, type License } from "../../../graphql.js";
import { fetchNumberOfFreeSeats } from "../../../redux/modules/users-admin.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import style from "../cards/styles/Card.module.scss";

const ApplicationInfoDialog = forwardRef<
  HTMLDivElement,
  Pick<TabDialogProps, "domProps" | "underlayProps" | "closeFn">
>(function ApplicationInfoDialog(props, ref) {
  const dialogRef = useDOMRef(ref);
  const [application, license, freeSeats] = useSelector<IAppState, [AppConfiguration | null, License | null, number]>(
    (state) => [
      state.config.configuration?.application,
      state.config.configuration?.license,
      state.usersAdmin.numberOfFreeSeats,
    ],
    shallowEqual,
  );
  const clientSetup = useMemo(() => getClientSetup(), []);
  const dispatch = useDispatch<ReduxDispatch>();
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  useEffect(() => {
    dispatch(fetchNumberOfFreeSeats());
  }, [dispatch]);

  return (
    <TabDialog
      ref={dialogRef}
      closeFn={props.closeFn}
      domProps={props.domProps}
      underlayProps={props.underlayProps}
      title={
        <div style={{ position: "relative", height: 40 }}>
          <img
            src={Logos.SelectorApplication}
            height={30}
            style={{ position: "absolute", left: 0, top: 5 }}
            alt="Selector"
          />
        </div>
      }
      width={420}
    >
      <TabItem key="application" title={"Application"} icon={"info"}>
        <Card scrollable noBorder contentStyle={{ paddingTop: 20 }}>
          <CardRowSimple label={"Version"}>{application?.version}</CardRowSimple>
          <CardRowSimple label={"Revision"}>{application?.revision}</CardRowSimple>
          <CardRowSimple label={"Buildstamp"}>{application?.buildstamp}</CardRowSimple>
          <CardRowSimple label={"Web server host"}>{application?.serverHost}</CardRowSimple>
          <CardRowSimple label={"Database host"}>{application?.databaseHost}</CardRowSimple>
          <CardRowSimple label={"Database service"}>{application?.databaseServiceName}</CardRowSimple>
          <CardRowSimple label={"Client OS"}>{clientSetup.clientOS}</CardRowSimple>
          <CardRowSimple label={"Client browser"}>{clientSetup.clientBrowser}</CardRowSimple>
          <CardRowSimple label={"Copyright"}>&#169; {currentYear} Genedata AG</CardRowSimple>
        </Card>
      </TabItem>
      <TabItem key="license" title={"License"} icon={"badge"}>
        <Card scrollable noBorder contentStyle={{ paddingTop: 20 }}>
          {license ? (
            <>
              <CardRowSimple label={"Licensed to"}>{license.customerName}</CardRowSimple>
              <CardRowSimple label={"License model"}>
                {`${freeSeats} / ${license.numberOfUsers} seats available`}
              </CardRowSimple>
              <CardRowSimple label={"Licensed modules"}>
                <div className={style.tags_list}>
                  {license.modules
                    ?.filter((module) => module != null)
                    .map((module) => (
                      <Tag key={module} intentColor={TagIntent.Secondary}>
                        {capitalizeFirstLetter(module)}
                      </Tag>
                    ))}
                </div>
              </CardRowSimple>
              <CardRowSimple label={"Valid until"}>
                {license.expirationDate
                  ? formatDateTimeFromISO(license.expirationDate, { hideTime: true })
                  : "perpetual"}
              </CardRowSimple>
            </>
          ) : (
            "No license registered"
          )}
        </Card>
      </TabItem>
    </TabDialog>
  );
});

export { ApplicationInfoDialog };
